import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useQuery } from 'react-query'
import { PageTitle, useLayout } from '../../../_metronic/layout/core'
import { CardsWidget171 } from '../../../_metronic/partials/widgets/_new/cards/CardsWidget171'
import { TablesWidgetURLScrubbingLogs } from '../../../_metronic/partials/widgets/_new/tables/TablesWidgetURLScrubbingLogs'
import { axiosClientAuth, URLS_QUERY_KEY } from '../../utils'
import {REGULATORY_OPERATOR_KEY} from '../../../config/countryConfig'
import { EngageWidgetTicket } from '../../../_metronic/partials/widgets/_new/engage/EngageWidgetTickets'

const fetchAnalyticsResources = async () => {
  const res = await axiosClientAuth.get('/analytics/resources/')
  const urlData = res.data?.data?.url ?? {}
  const urlByOperator = urlData.by_operator?.[REGULATORY_OPERATOR_KEY] ?? {}
  return {
    whitelisted: urlByOperator.whitelisted ?? 0,
    blacklisted: urlByOperator.blacklisted ?? 0,
    pending: urlByOperator.pending ?? 0,
  }
}

const fetchUrls = async (
  page: number,
  pageSize: number,
  search: string,
  fromDate: string,
  toDate: string,
  type: string,
  status: string
) => {
  const res = await axiosClientAuth.get('/urls/', {
    params: {
      page,
      page_size: pageSize,
      ...(search ? { search } : {}),
      ...(fromDate ? { from_date: fromDate } : {}),
      ...(toDate ? { to_date: toDate } : {}),
      ...(type ? { type } : {}),
      ...(status ? { url_status: status } : {}),
    },
    transformResponse: [
      (data: string) => {
        // Preserve large integer owner_id as string to avoid JS precision loss (> MAX_SAFE_INTEGER)
        const safe = data.replace(/"owner_id"\s*:\s*(\d{15,})/g, '"owner_id":"$1"')
        return JSON.parse(safe)
      },
    ],
  })
  const raw = res.data.data || {}
  const results = (raw.results || []).map((item: any) => ({
    uuid: item.id,
    url: item.registered_url,
    name: item.title,
    pe_name: item.owner_name ?? '--',
    pe_id: item.owner_id,
    is_trusted: item.type === 'trusted' ? true : item.type === null ? null : false,
    is_blacklisted_by_tcil:
      item.status?.[REGULATORY_OPERATOR_KEY]?.active === true
        ? false
        : item.status?.[REGULATORY_OPERATOR_KEY]?.active === false
        ? true
        : null,
    created_at: item.created_at,
    updated_at: item.updated_at,
    redirect: item.redirect ?? null,
    redirection: item.redirection ?? null,
    snapshot: item.snapshot ?? null,
  }))
  return {
    urls: results,
    totalPages: raw.total_pages ?? 1,
    totalCount: raw.count ?? results.length,
  }
}

const myBreadCrumbs: any = [
  {
    title: 'Home',
    path: '/dashboard',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]

export default function UrlVerification() {
  const intl = useIntl()
  const { setRightTolbar } = useLayout()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [urlType, setUrlType] = useState('')
  const [urlStatus, setUrlStatus] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  const { data: urlData, isLoading: urlLoading } = useQuery(
    [URLS_QUERY_KEY, page, pageSize, debouncedSearch, fromDate, toDate, urlType, urlStatus],
    () => fetchUrls(page, pageSize, debouncedSearch, fromDate, toDate, urlType, urlStatus),
    { keepPreviousData: true }
  )
  const { data: analyticsData, isLoading: analyticsLoading } = useQuery(
    ['analytics-resources'],
    fetchAnalyticsResources
  )

  useEffect(() => {
    setRightTolbar('')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const whitelisted = intl.formatMessage({ id: 'COMMON.WHITELISTED', defaultMessage: 'Whitelisted' })
  const blacklisted = intl.formatMessage({ id: 'COMMON.BLACKLISTED', defaultMessage: 'Blacklisted' })
  const pending = intl.formatMessage({ id: 'COMMON.PENDING', defaultMessage: 'Pending' })

  return (
    <>
      <PageTitle breadcrumbs={myBreadCrumbs}>{intl.formatMessage({ id: 'PAGE.URL_VERIFICATION', defaultMessage: 'URL Verification' })}</PageTitle>
      <div className='row g-5 g-xl-10 mb-5 mb-xl-10'>
        <div className='col-md-12 col-lg-12 col-xl-6'>
          {analyticsLoading ? (
            <div className='card h-lg-100 h-100 d-flex align-items-center justify-content-center'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : analyticsData && (
            <CardsWidget171
              className='h-lg-100 h-100'
              mLables={{
                [whitelisted]: analyticsData.whitelisted,
                [blacklisted]: analyticsData.blacklisted,
                [pending]: analyticsData.pending,
              }}
              mColors={{ [whitelisted]: 'bg-success', [blacklisted]: 'bg-danger', [pending]: 'bg-warning' }}
              heading={intl.formatMessage({ id: 'URL.SUMMARY', defaultMessage: 'URL Summary' })}
              subHeading='URLs'
            />
          )}
        </div>

        <div className='col-md-12 col-lg-12 col-xl-6'>
          <EngageWidgetTicket className='h-md-100 bg-primary' />
        </div>
      </div>

      <div className='row g-5 g-xl-10 mb-5 mb-xl-10'>
        <div className='col-lg-12'>
          <TablesWidgetURLScrubbingLogs
              className='card-xxl-stretch mb-5 mb-xl-10'
              urlData={urlData?.urls ?? []}
              loading={urlLoading}
              serverPage={page}
              serverPageSize={pageSize}
              serverTotalPages={urlData?.totalPages}
              serverTotalCount={urlData?.totalCount}
              onServerPageChange={setPage}
              onServerPageSizeChange={(s) => { setPageSize(s); setPage(1) }}
              onServerSearchChange={(s) => { setSearch(s); setPage(1) }}
              onServerDateChange={(from, to) => { setFromDate(from); setToDate(to); setPage(1) }}
              serverType={urlType}
              onServerTypeChange={(t) => { setUrlType(t); setPage(1) }}
              serverStatus={urlStatus}
              onServerStatusChange={(s) => { setUrlStatus(s); setPage(1) }}
            />
        </div>
      </div>
    </>
  )
}